import type {
  SmsSegmentType, SmsScheduleType, SmsCampaignStatus, SmsGateReason, SmsApprovalStatus,
} from "@wedding/shared";

/**
 * SMS kampanya etiket + rozet sözlüğü — TEK KAYNAK.
 *
 * Neden ayrı dosya: bu değerleri İKİ ekran çiziyor — organizer/admin ortak
 * `SmsCampaignsPage` (düğün-içi) ve admin `sms-center` (çapraz-düğün). Kopyalasaydık
 * yeni bir `status` eklendiğinde biri güncellenip diğeri sessizce eski kalırdı:
 * aynı kampanya iki ekranda FARKLI renkte görünürdü. Burada tek yerde durunca
 * eksik üye derleme hatası olur (aşağıdaki switch'lerde `default` dalı YOK).
 */

/**
 * `t(\`sms.segments.${s}\`)` yerine exhaustive switch.
 *
 * Neden: şablon çağrısını `pnpm check:i18n-keys` yalnız NAMESPACE seviyesinde
 * denetleyebiliyordu (`sms.segments` var mı?) — `sms.segments.manual` silinse
 * sessiz kalırdı. Her dal literal olduğu için artık TAM ANAHTAR denetlenir.
 * `default` dalı YOK: union bir üye kazanırsa tsc "lacks ending return" der.
 */
export function segmentLabel(t: (key: string) => string, segment: SmsSegmentType): string {
  switch (segment) {
    case "all":        return t("sms.segments.all");
    case "rsvp_none":  return t("sms.segments.rsvp_none");
    case "rsvp_no":    return t("sms.segments.rsvp_no");
    case "rsvp_maybe": return t("sms.segments.rsvp_maybe");
    case "manual":     return t("sms.segments.manual");
  }
}

/** `segmentLabel` ile aynı gerekçe — bkz. yukarısı. */
export function scheduleLabel(t: (key: string) => string, schedule: SmsScheduleType): string {
  switch (schedule) {
    case "now":        return t("sms.schedules.now");
    case "scheduled":  return t("sms.schedules.scheduled");
    case "auto_chain": return t("sms.schedules.auto_chain");
  }
}

/**
 * `sms_campaigns.hold_reason` (migration 045) → organizatörün anlayacağı açıklama.
 *
 * `segmentLabel` ile aynı gerekçe (exhaustive switch, `default` dalı YOK): ham
 * enum ekrana ASLA sızmamalı ve union bir üye kazanırsa tsc burada patlamalı.
 */
export function holdReasonLabel(t: (key: string) => string, reason: SmsGateReason): string {
  switch (reason) {
    case "channel_disabled":   return t("sms.holdReasons.channel_disabled");
    case "campaigns_disabled": return t("sms.holdReasons.campaigns_disabled");
    case "not_configured":     return t("sms.holdReasons.not_configured");
    case "spend_breaker":      return t("sms.holdReasons.spend_breaker");
    case "daily_cap":          return t("sms.holdReasons.daily_cap");
  }
}

/**
 * API'den gelen ham `hold_reason`ı union'a daralt.
 *
 * Neden gerekli: kolon serbest TEXT (status CHECK'ine dokunmamak için bilinçli
 * tercih — bkz. migration 045). Eski/bilinmeyen bir değer gelirse çevirisi
 * olmayan bir anahtar aramak yerine rozeti hiç çizmeyiz.
 */
const HOLD_REASONS: readonly SmsGateReason[] = [
  "channel_disabled", "campaigns_disabled", "not_configured", "spend_breaker", "daily_cap",
];
export function toHoldReason(raw: string | null | undefined): SmsGateReason | null {
  return raw != null && (HOLD_REASONS as readonly string[]).includes(raw) ? (raw as SmsGateReason) : null;
}

/**
 * `sms_campaigns.approval_status` (migration 047) → rozet etiketi.
 *
 * `holdReasonLabel` ile AYNI gerekçe (exhaustive switch, `default` YOK): union
 * bir üye kazanırsa tsc burada patlar, ham enum ekrana sızmaz.
 *
 * ⚠️ `hold_reason` ile ORTOGONAL — bir kampanya aynı anda hem "Beklemede"
 * (platform engeli) hem "Onay Bekliyor" (insan kararı) olabilir; ikisi AYRI
 * rozet olarak çizilir, biri diğerinin yerini TUTMAZ.
 */
export function approvalStatusLabel(t: (key: string) => string, status: SmsApprovalStatus): string {
  switch (status) {
    case "pending":  return t("sms.approval.pendingBadge");
    case "approved": return t("sms.approval.approvedBadge");
    case "rejected": return t("sms.approval.rejectedBadge");
  }
}

/** `toHoldReason` ile aynı gerekçe: kolon serbest TEXT (CHECK YOK) → allowlist. */
const APPROVAL_STATUSES: readonly SmsApprovalStatus[] = ["pending", "approved", "rejected"];
export function toApprovalStatus(raw: string | null | undefined): SmsApprovalStatus | null {
  return raw != null && (APPROVAL_STATUSES as readonly string[]).includes(raw)
    ? (raw as SmsApprovalStatus)
    : null;
}

/** `sms_campaigns.status` → etiket. Diğerleriyle aynı exhaustive gerekçe. */
export function campaignStatusLabel(t: (key: string) => string, status: SmsCampaignStatus): string {
  switch (status) {
    case "scheduled":   return t("sms.status.scheduled");
    case "active":      return t("sms.status.active");
    case "dispatching": return t("sms.status.dispatching");
    case "completed":   return t("sms.status.completed");
    case "canceled":    return t("sms.status.canceled");
    case "failed":      return t("sms.status.failed");
  }
}

/** `toHoldReason` ile aynı gerekçe — API ham TEXT döndürür. */
export const CAMPAIGN_STATUSES: readonly SmsCampaignStatus[] = [
  "scheduled", "active", "dispatching", "completed", "canceled", "failed",
];
export function toCampaignStatus(raw: string | null | undefined): SmsCampaignStatus | null {
  return raw != null && (CAMPAIGN_STATUSES as readonly string[]).includes(raw)
    ? (raw as SmsCampaignStatus)
    : null;
}

/**
 * Durum → rozet sınıfları. `Record<SmsCampaignStatus, string>` olduğu için
 * union'a yeni üye eklendiğinde tsc eksik anahtarı burada da yakalar.
 */
export const STATUS_BADGE: Record<SmsCampaignStatus, string> = {
  scheduled:   "bg-sky-500/10 text-sky-600 dark:text-sky-400 ring-sky-500/20",
  active:      "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-emerald-500/20",
  dispatching: "bg-amber-500/10 text-amber-600 dark:text-amber-400 ring-amber-500/20",
  completed:   "bg-muted/60 text-muted-foreground ring-border",
  canceled:    "bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-rose-500/20",
  failed:      "bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-rose-500/20",
};

/** Onay durumu → rozet sınıfları. `STATUS_BADGE` ile aynı exhaustive koruma. */
export const APPROVAL_BADGE: Record<SmsApprovalStatus, string> = {
  pending:  "bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 ring-indigo-500/20",
  approved: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-emerald-500/20",
  rejected: "bg-rose-500/10 text-rose-600 dark:text-rose-400 ring-rose-500/20",
};
